import api from './client'

export interface RegisterPayload {
  email: string
  password: string
  firstName?: string
  lastName?: string
  dateOfBirth?: string
  mobilePhone?: string
  consentTerms: true
  consentPersonalData: true
  consentMarketing?: boolean
  consentAdminAccess?: boolean
  referralCode?: string
  invitationToken?: string
  consentProfiling?: boolean
  consentAssociationMarketing?: boolean
  locale?: string
  turnstileToken?: string
  visitorId?: string
}

export interface LoginPayload {
  identifier?: string  // email or phone (+39...) — preferred
  email?: string       // legacy field — backward compat for E2E and older clients
  password: string
  mfaCode?: string     // codice TOTP/email (6 cifre) o recovery code, al 2° passo del login 2FA
}

export interface LoginResponse {
  accessToken: string
  refreshToken: string
  tokenType: string
  expiresIn: number
}

// Quando l'account ha il 2FA attivo, il login risponde 200 con questo payload
// (nessun token): il client richiede il codice e ripresenta /login con mfaCode.
export interface MfaChallengeResponse {
  requiresMfa: true
  mfaMethod: 'totp' | 'email'
  tempToken: string
}

export type LoginResult = LoginResponse | MfaChallengeResponse

export function isMfaChallenge(r: LoginResult): r is MfaChallengeResponse {
  return (r as MfaChallengeResponse).requiresMfa === true
}

export interface RegisterResponse {
  userId: string
  message: string
}

export interface MeResponse {
  id: string
  email: string
  status: string
  mfaEnabled: boolean
  roles: string[]
  createdAt: string
  walletAddress: string | null
  consents: Array<{ type: string; granted: boolean; timestamp: string }>
}

export interface OAuthAuthorizeResponse {
  authUrl: string
  state: string
}

export interface OAuthExchangeResponse extends LoginResponse {
  isNewUser: boolean
}

export const authApi = {
  register: (data: RegisterPayload, config?: Record<string, unknown>) =>
    api.post<RegisterResponse>('/v1/auth/register', data, config as any).then((r) => r.data),

  login: (data: LoginPayload, config?: Record<string, unknown>) =>
    api.post<LoginResult>('/v1/auth/login', data, config as any).then((r) => r.data),

  me: () => api.get<MeResponse>('/v1/users/me').then((r) => r.data),

  // Reinvia l'email di verifica (pubblico, rate-limited lato IAM). Prima era
  // chiamato solo dall'admin: ora lo usa anche l'utente dallo step di verifica.
  resendVerification: (email: string) =>
    api.post<{ message: string }>('/v1/auth/resend-verification', { email }).then((r) => r.data),

  logout: () => api.post('/v1/auth/logout').catch(() => null),

  refresh: (refreshToken: string) =>
    api.post<{ accessToken: string; refreshToken: string }>(
      '/v1/auth/token/refresh',
      { refreshToken }
    ).then((r) => r.data),

  oauthAuthorize: (provider: 'google') =>
    api.get<OAuthAuthorizeResponse>(`/v1/auth/oauth/authorize?provider=${provider}`).then((r) => r.data),

  oauthExchange: (data: { provider: 'google'; code: string; state: string; consentTerms?: boolean }) =>
    api.post<OAuthExchangeResponse>('/v1/auth/oauth/exchange', data).then((r) => r.data),

  web3Nonce: (address: string) =>
    api.post<{ message: string; nonce: string }>('/v1/auth/web3/nonce', { address }).then((r) => r.data),

  web3Verify: (data: { address: string; signature: string; message: string }) =>
    api.post<{ accessToken: string; refreshToken: string; user: { id: string; walletAddress: string; roles: string[]; isNewUser: boolean } }>(
      '/v1/auth/web3/verify', data
    ).then((r) => r.data),

  web3Link: (data: { address: string; signature: string; message: string }) =>
    api.post<{ walletAddress: string }>('/v1/auth/web3/link', data).then((r) => r.data),

  web3Unlink: () =>
    api.delete('/v1/auth/web3/link'),

  forgotPassword: (email: string, turnstileToken?: string) =>
    api.post<{ message: string }>('/v1/auth/password/reset', { email, turnstileToken }).then((r) => r.data),

  resetPassword: (token: string, password: string) =>
    api.post<{ message: string }>('/v1/auth/password/reset/confirm', { token, password }).then((r) => r.data),

  sendSpeciesContributionThanks: (contributions: Array<{ scientificName: string; commonName?: string }>) =>
    api.post<{ message: string }>('/v1/auth/email/species-contribution-thanks', { contributions }).then((r) => r.data),

  // ── MFA ────────────────────────────────────────────────────────────────────
  mfaSetup: (method: 'totp' | 'email') =>
    api.post<{ method: string; secret?: string; qrCodeUrl?: string; message?: string }>(
      '/v1/auth/mfa/setup', { method }
    ).then((r) => r.data),

  mfaVerify: (code: string, method?: 'totp' | 'email') =>
    api.post<{ mfaEnabled: boolean; method: string }>(
      '/v1/auth/mfa/verify', { code, method }
    ).then((r) => r.data),

  mfaDisable: (code: string) =>
    api.delete<{ mfaEnabled: boolean }>('/v1/auth/mfa', { data: { code } }).then((r) => r.data),

  mfaEmailResend: (tempToken: string) =>
    api.post<{ message: string }>('/v1/auth/mfa/email/resend', { tempToken }).then((r) => r.data),
}
